import { buildDataProvider } from 'react-admin-amplify';

import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

import { API } from 'aws-amplify';
import { Auth } from 'aws-amplify';
import { convertToAWSUTC, parseCronExpression } from '../utils';
import { USER_ROLE } from '../constant';

const originalDataProvider = buildDataProvider(
  {
    queries,
    mutations,
  },
  {
    authMode: 'AMAZON_COGNITO_USER_POOLS',
    enableAdminQueries: true,
  },
);

const dataProvider = {
  ...originalDataProvider,
  getList: async (resource, params) => {
    switch (resource) {
      case "logs": {
        try {
          const { page, perPage } = params.pagination; // pagination param
          const { site, date_gte, date_lte } = params.filter || {}; // filter param
          
          let nextToken = null;
          let items = [];
          let fetchedCount = 0;

          // call API
          const response = await API.graphql({
            query: queries.listRecordsByUpdatedAt,
            variables: {
              type: 'Record',
              sortDirection: 'DESC',
            },
          });

          const allItems = response.data.listRecordsByUpdatedAt.items || [];

          // DateRange filter (date_gte, date_lte)
          const filteredItems = allItems.filter((record) => {
            const updatedAt = new Date(record.updatedAt);
            const isAfterStartDate = date_gte ? updatedAt >= new Date(date_gte) : true;
            const isBeforeEndDate = date_lte ? updatedAt <= new Date(date_lte) : true;

            return isAfterStartDate && isBeforeEndDate;
          });

          // Site name filter
          const filteredBySite = filteredItems.filter((record) => {
            return site ? record.schedule?.route?.site?.name.toLowerCase().includes(site.toLowerCase()) : true;
          });

          // pagination navigation
          const total = filteredBySite.length;
          const paginatedItems = filteredBySite.slice((page - 1) * perPage, page * perPage);

          return {
            data: paginatedItems,
            total,
          };
        } catch (error) {
          console.error('Error fetching reports:', error);
          throw new Error('Failed to fetch reports');
        }
      }
      case "reports": {
        try {
          const { page, perPage } = params.pagination; // pagination param
          const { site, date_gte, date_lte } = params.filter || {}; // filter param
          
          let nextToken = null;
          let items = [];
          let fetchedCount = 0;

          // call API
          const response = await API.graphql({
            query: queries.listRecordsByUpdatedAt,
            variables: {
              type: 'Record',
              sortDirection: 'DESC',
            },
          });

          const allItems = response.data.listRecordsByUpdatedAt.items || [];

          // DateRange filter (date_gte, date_lte)
          const filteredItems = allItems.filter((record) => {
            const updatedAt = new Date(record.updatedAt);
            const isAfterStartDate = date_gte ? updatedAt >= new Date(date_gte) : true;
            const isBeforeEndDate = date_lte ? updatedAt <= new Date(date_lte) : true;

            return isAfterStartDate && isBeforeEndDate;
          });

          // Site name filter
          const filteredBySite = filteredItems.filter((record) => {
            return site ? record.schedule?.route?.site?.name.toLowerCase().includes(site.toLowerCase()) : true;
          });

          // pagination navigation
          const total = filteredBySite.length;
          const paginatedItems = filteredBySite.slice((page - 1) * perPage, page * perPage);

          return {
            data: paginatedItems,
            total,
          };
        } catch (error) {
          console.error('Error fetching reports:', error);
          throw new Error('Failed to fetch reports');
        }
      }
      case "overview": {
        const { filter, sort, pagination: { page, perPage } } = params;

        const filterTime = convertToAWSUTC(filter?.selected_date);
        try {
          const currentUser = await Auth.currentAuthenticatedUser();
          const isAdmin = currentUser?.signInUserSession.idToken?.payload["cognito:groups"]?.includes(USER_ROLE.AppAdmin);
          const overviewFilter = {
            datetime: {
              ge: filterTime.startUTC,
              le: filterTime.endUTC
            },
            ...(isAdmin ? {} : { operator_ids: { contains: currentUser?.attributes.sub } }),
          };

          let items = [];
          let nextToken = null;
          do {
            const response = await API.graphql({
              query: queries.listPatrols,
              variables: {
                filter: overviewFilter,
                limit: 1000,
                nextToken,
              },
            });
            const data = response.data.listPatrols;
            items = items.concat(data.items || []);
            nextToken = data.nextToken;
          } while (nextToken);

          if (items.length === 0) {
            return {
              data: [],
              total: 0,
            };
          }

          // Only keep records with schedule.deleted === false
          const filteredRecords = items.filter(item => item.schedule && item.schedule.deleted === false);

          // Map and parse cron only once per record
          const records = filteredRecords.map((record) => {
            const cronParsed = parseCronExpression(record.schedule.cron_expression);
            return {
              ...record,
              time: cronParsed?.time || '-',
              time_sort: cronParsed?.time_sort || 0,
            };
          }).sort((a,b) => {
            switch (sort?.order) {
              case 'ASC':
                return a.time_sort - b.time_sort;
              case 'DESC':
                return b.time_sort - a.time_sort;
              default:
                return a.time_sort - b.time_sort;
            }
          });
          
          // slice the records to the page and perPage
          const paginatedItems = records.slice((page - 1) * perPage, page * perPage);

          return {
            data: paginatedItems,
            total: records.length,
          };
          } catch (error) {
          console.error("Error fetching overview:", error);
          throw new Error("Failed to fetch overview");
        }
      }
      case "routes": {
        try {
          const { page = 1, perPage = 10 } = params.pagination || {};
          const { site } = params.filter || {};

          const siteName = site?.name || '';
          
          // Calculate how many items we need to skip
          const itemsToSkip = (page - 1) * perPage;
          
          let allItems = [];
          let nextToken = null;
          let fetchedCount = 0;

          // Fetch items until we have enough for the requested page
          do {
            const response = await API.graphql({
              query: queries.listRoutesByUpdatedAt,
              variables: { 
                type: "Route", 
                sortDirection: "DESC", 
                nextToken,
                limit: Math.max(perPage, 50) // Fetch at least 50 items per request
              },
            });

            const data = response.data.listRoutesByUpdatedAt;
            
            if (data.items && data.items.length > 0) {
              allItems = [...allItems, ...data.items];
              fetchedCount += data.items.length;
            }
            
            nextToken = data.nextToken;
            
            // Stop if we have enough items for the current page + some buffer for total count
            if (fetchedCount >= itemsToSkip + perPage * 2) {
              break;
            }
            
          } while (nextToken);

          allItems = allItems.filter((item) => item.site?.name?.toLowerCase().includes(siteName.toLowerCase()));

          // If we need to continue fetching for accurate total (optional)
          let estimatedTotal = allItems.length;
          if (nextToken) {
            // There are more items, so total is at least what we have
            estimatedTotal = allItems.length + 1; // Conservative estimate
          }

          // Get the page slice
          const paginatedItems = allItems.slice(itemsToSkip, itemsToSkip + perPage);

          return {
            data: paginatedItems,
            total: estimatedTotal,
          };

        } catch (error) {
          console.error("Error fetching routes:", error);
          throw new Error("Failed to fetch routes");
        }
      }
      case "checkpoints": {
        try {
          const { page = 1, perPage = 10 } = params.pagination || {};
          const { site_id, site } = params.filter || {};

          const siteName = site?.name || '';
      
          // Build filter
          const filter = site_id ? { site_id: { eq: site_id } } : {};
      
          // Calculate how many items we need to skip
          const itemsToSkip = (page - 1) * perPage;
          
          let allItems = [];
          let nextToken = null;
          let fetchedCount = 0;
      
          // Fetch items until we have enough for the requested page
          do {
            const response = await API.graphql({
              query: queries.listCheckpointsByUpdatedAt,
              variables: { 
                type: "Checkpoint", 
                sortDirection: "DESC", 
                filter,
                nextToken,
                limit: Math.max(perPage, 50) // Fetch at least 50 items per request
              },
            });
      
            const data = response.data.listCheckpointsByUpdatedAt;
            
            if (data.items && data.items.length > 0) {
              allItems = [...allItems, ...data.items];
              fetchedCount += data.items.length;
            }
            
            nextToken = data.nextToken;
            
            // Stop if we have enough items for the current page + some buffer for total count
            if (fetchedCount >= itemsToSkip + perPage * 2) {
              break;
            }
            
          } while (nextToken);

          allItems = allItems.filter((item) => item.site?.name?.toLowerCase().includes(siteName.toLowerCase()));
      
          // If we need to continue fetching for accurate total (optional)
          let estimatedTotal = allItems.length;
          if (nextToken) {
            // There are more items, so total is at least what we have
            estimatedTotal = allItems.length + 1; // Conservative estimate
          }
      
          // Get the page slice
          const paginatedItems = allItems.slice(itemsToSkip, itemsToSkip + perPage);
      
          return {
            data: paginatedItems,
            total: estimatedTotal,
          };
      
        } catch (error) {
          console.error("Error fetching checkpoints:", error);
          throw new Error("Failed to fetch checkpoints");
        }
      }
      case "route_checkpoints": {
        try {
          const { route_id } = params.filter;
      
          let items = [];
          let nextToken = null;
      
          do {
            const response = await API.graphql({
              query: queries.listCheckpointsByRoute,
              variables: {
                route_id,
                nextToken,
              },
            });
      
            const data = response.data.listCheckpointsByRoute;
            const newItems = data.items || [];
            nextToken = data.nextToken;
      
            items = items.concat(newItems);
      
          } while (nextToken);

          return {
            data: items,
            total: items.length,
          };
        } catch (error) {
          console.error("Error fetching route checkpoints:", error);
          throw new Error("Failed to fetch route checkpoints");
        }
      }
      case "schedules": {
        const response = await API.graphql({
          query: queries.listSchedulesByUpdatedAt,
          variables: { type: "Schedule", sortDirection: "DESC", filter: { deleted: { eq: false } } },
        });

        if (!response.data.listSchedulesByUpdatedAt) {
          console.error("No schedules found");
          return {
            data: [],
            total: 0,
          };
        }

        const allItems = response.data.listSchedulesByUpdatedAt.items || [];

        try {
          const { page, perPage } = params.pagination;
          let nextToken = null;
          let items = [];
          let fetchedCount = 0;

          while (fetchedCount < page * perPage) {
            const response = await API.graphql({
              query: queries.listSchedulesByUpdatedAt,
              variables: { 
                type: "Schedule", 
                sortDirection: "DESC", 
                filter: { deleted: { eq: false } },
                limit: perPage, // Fetch only required items
                nextToken // Use pagination token,
              },
            });
  
            const data = response.data.listSchedulesByUpdatedAt;
            items = [...items, ...data.items];
            nextToken = data.nextToken;
            fetchedCount += data.items.length;
  
            if (!nextToken) break; // Stop if no more data
          }

          const paginatedItems = items.slice((page - 1) * perPage, page * perPage)

          return {
            data: paginatedItems,
            total: allItems?.length,
          };
        } catch (error) {
          console.error("Error fetching schedules:", error);
          throw new Error("Failed to fetch schedules");
        }
      }
      case "cameras": {
        try {
          const { page, perPage } = params.pagination;
          let nextToken = null;
          let items = [];
          let fetchedCount = 0;

          const response = await API.graphql({
            query: queries.listCamerasBySite,
            variables: { site_id: params.filter.site_id },
          });

          const allItems = response.data.listCamerasBySite.items || [];

          while (fetchedCount < page * perPage) {
            const response = await API.graphql({
              query: queries.listCamerasBySite,
              variables: { site_id: params.filter.site_id, limit: perPage, nextToken },
            });

            const data = response.data.listCamerasBySite;
            items = [...items, ...data.items];
            nextToken = data.nextToken;
            fetchedCount += data.items.length;

            if (!nextToken) break; // Stop if no more data
          }

          const paginatedItems = items.slice((page - 1) * perPage, page * perPage);

          return {
            data: paginatedItems,
            total: allItems?.length,
          };
        } catch (error) {
          console.error("Error fetching cameras:", error);
          throw new Error("Failed to fetch cameras");
        }
      }
      default:
        return originalDataProvider.getList(resource, params);
    }
  },
  getOne: (resource, params) => {
    switch (resource) {
      case "logs":
        return originalDataProvider.getOne("records", params);
      case "reports":
        return originalDataProvider.getOne("records", params);
      case "overview":
        return originalDataProvider.getOne("schedules", params);
      default:
        return originalDataProvider.getOne(resource, params);
    }
  },
  getMany: (resource, params) => {
    switch (resource) {
      case "logs":
        return originalDataProvider.getMany("records", params);
      case "reports":
        return originalDataProvider.getMany("records", params);
      case "overview":
        return originalDataProvider.getMany("schedules", params);
      default:
        return originalDataProvider.getMany(resource, params);
    }
  },
  getManyReference: (resource, params) => {
    switch (resource) {
      case "logs":
        return originalDataProvider.getManyReference("records", params);
      case "reports":
        return originalDataProvider.getManyReference("records", params);
      case "overview":
        return originalDataProvider.getManyReference("schedules", params);
      default:
        return originalDataProvider.getManyReference(resource, params);
    }
  },
};

export default dataProvider;
export { dataProvider };